import { useRef, useCallback } from 'react';
import { UseFormSetValue, Path, PathValue } from 'react-hook-form';
import { hasImagesChanged } from 'src/helper/translationHelper';
import { TranslationFormData } from 'src/validations/translationFormSchema';
import { QUESTION_FIELD_NAMES } from 'src/contstant/commonLabels';

/** Top-level string fields that can contain images */
const TEXT_EDITOR_FIELDS = [
  QUESTION_FIELD_NAMES.TITLE,
  QUESTION_FIELD_NAMES.QUESTION_TEXT,
  QUESTION_FIELD_NAMES.AUDIT_INSTRUCTION,
  QUESTION_FIELD_NAMES.PASS_CRITERIA,
  QUESTION_FIELD_NAMES.FAIL_CRITERIA,
  QUESTION_FIELD_NAMES.JUSTIFICATION,
  QUESTION_FIELD_NAMES.TASK_NAME,
  QUESTION_FIELD_NAMES.TASK_DESCRIPTION,
] as const;

type PreviousValuesMap = Partial<Record<Path<TranslationFormData>, string>>;

/** Extracts string values from form data for the text editor fields */
const extractStringValues = (formData: TranslationFormData): PreviousValuesMap => {
  const values: PreviousValuesMap = {};
  TEXT_EDITOR_FIELDS.forEach((field) => {
    const value = formData[field as keyof TranslationFormData];
    if (typeof value === 'string') {
      values[field as Path<TranslationFormData>] = value;
    }
  });
  return values;
};

interface UseImageChangeDetectionProps {
  initialFormData: TranslationFormData;
  setValue: UseFormSetValue<TranslationFormData>;
  onImageChangeDetected: () => void;
}

export const useImageChangeDetection = ({
  initialFormData,
  setValue,
  onImageChangeDetected,
}: UseImageChangeDetectionProps) => {
  const isRevertingRef = useRef(false);
  const previousValidValuesRef = useRef<PreviousValuesMap>(
    extractStringValues(initialFormData),
  );

  const handleTextEditorChange = useCallback(
    (fieldName: string, newValue: string) => {
      if (isRevertingRef.current) return;

      const fieldPath = fieldName as Path<TranslationFormData>;
      const previousValue =
        previousValidValuesRef.current[fieldPath] ||
        (initialFormData[fieldName as keyof TranslationFormData] as string);

      if (previousValue && hasImagesChanged(previousValue, newValue)) {
        isRevertingRef.current = true;
        onImageChangeDetected();
        setValue(
          fieldPath,
          previousValue as PathValue<TranslationFormData, typeof fieldPath>,
        );
        setTimeout(() => {
          isRevertingRef.current = false;
        }, 100);
      } else {
        previousValidValuesRef.current[fieldPath] = newValue;
      }
    },
    [initialFormData, setValue, onImageChangeDetected],
  );

  const resetPreviousValues = useCallback((formData: TranslationFormData) => {
    previousValidValuesRef.current = extractStringValues(formData);
  }, []);

  return {
    handleTextEditorChange,
    resetPreviousValues,
  };
};
